import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { extractErr } from "../lib/api";
import { MessageSquare, Link2 } from "lucide-react";
import { toast } from "sonner";

const CH_LABEL = { whatsapp: "WhatsApp", messenger: "Messenger", instagram: "Instagram" };
const CH_STYLE = {
  whatsapp: "bg-emerald-50 text-emerald-800 border-emerald-200",
  messenger: "bg-blue-50 text-blue-800 border-blue-200",
  instagram: "bg-pink-50 text-pink-800 border-pink-200",
};

export const isSocialLead = (l) => (l?.phone || "").startsWith("fb:") || (l?.phone || "").startsWith("ig:");

const leadChannels = (l) => {
  const ids = [l.phone, ...(l.social_ids || [])];
  const out = new Set();
  ids.forEach(p => {
    if ((p || "").startsWith("fb:")) out.add("messenger");
    else if ((p || "").startsWith("ig:")) out.add("instagram");
    else if ((p || "").startsWith("+")) out.add("whatsapp");
  });
  return Array.from(out);
};

export const ChannelBadges = ({ lead }) => (
  <span className="inline-flex flex-wrap gap-1" data-testid={`channel-badges-${lead.id}`}>
    {leadChannels(lead).map(c => (
      <span key={c} className={`text-[9px] font-semibold px-1.5 py-0.5 border rounded-sm ${CH_STYLE[c]}`}>{CH_LABEL[c]}</span>
    ))}
  </span>
);

export const OpenChatButton = ({ lead, className = "" }) => {
  const [convs, setConvs] = useState(null);
  const navigate = useNavigate();
  const go = (id) => navigate(`/inbox?c=${id}`);
  const open = async () => {
    try {
      const { data } = await api.get(`/leads/${lead.id}/conversations`);
      if (data.length === 0) toast.error("该客户还没有聊天记录");
      else if (data.length === 1) go(data[0].id);
      else setConvs(data);
    } catch (e) { toast.error(extractErr(e)); }
  };
  return (
    <span className="relative inline-block">
      <button data-testid={`open-chat-${lead.id}`} onClick={open} className={`inline-flex items-center gap-1 text-emerald-700 hover:underline font-semibold ${className}`}>
        <MessageSquare size={11} /> 打开聊天
      </button>
      {convs && (
        <div className="absolute z-20 mt-1 left-0 bg-white border border-slate-200 shadow-lg rounded-sm min-w-[150px]" data-testid={`chat-picker-${lead.id}`}>
          {convs.map(c => (
            <button key={c.id} data-testid={`chat-pick-${c.channel}-${lead.id}`} onClick={() => go(c.id)} className="block w-full text-left px-3 py-2 text-xs hover:bg-slate-50">
              {CH_LABEL[c.channel] || c.channel}{c.archived ? " · 已归档" : ""}
            </button>
          ))}
          <button onClick={() => setConvs(null)} className="block w-full text-left px-3 py-1.5 text-[10px] text-slate-400 border-t border-slate-100">关闭</button>
        </div>
      )}
    </span>
  );
};

export const LinkPhoneModal = ({ lead, onClose, onDone }) => {
  const [phone, setPhone] = useState("+60");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/link-phone`, { phone });
      toast.success(data.merged ? `已合并到现有客户「${data.lead.name}」` : "已关联 WhatsApp 号码");
      onDone();
    } catch (e) { toast.error(extractErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-950/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-sm rounded-sm shadow-xl p-5 space-y-3" onClick={e => e.stopPropagation()} data-testid="link-phone-modal">
        <div className="font-display text-lg font-bold flex items-center gap-2"><Link2 size={16} /> 关联 WhatsApp 号码</div>
        <p className="text-xs text-slate-500">为「{lead.name}」填上手机号。如果这个号码已经是 WhatsApp 客户，两边的聊天记录会合并成同一个客户。</p>
        <input data-testid="link-phone-input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+60123456789"
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm font-mono focus:outline-none focus:border-emerald-700" />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-slate-300 rounded-sm">取消</button>
          <button data-testid="link-phone-submit" disabled={busy} onClick={submit} className="px-3 py-1.5 text-sm bg-slate-950 text-white rounded-sm hover:bg-emerald-700 disabled:opacity-50">
            {busy ? "处理中…" : "确认关联"}
          </button>
        </div>
      </div>
    </div>
  );
};

export const LinkPhoneButton = ({ lead, onClick }) => isSocialLead(lead) ? (
  <button data-testid={`link-phone-btn-${lead.id}`} onClick={() => onClick(lead)} className="inline-flex items-center gap-1 text-[10px] text-blue-700 hover:underline font-semibold">
    <Link2 size={10} /> 关联 WhatsApp
  </button>
) : null;
